import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMonetization } from '../contexts/MonetizationContext';
import DisAvatar from '../components/design-system/DisAvatar';
import FeedbackButtons from '../components/FeedbackButtons';
import HistoryService from '../services/HistoryService';
import FloatingMenu from '../components/FloatingMenu';

// Cache pour les histoires (persistant entre les navigations)
const storiesCache = {
  data: [],
  timestamp: null,
  childFilter: null,
  CACHE_DURATION: 5 * 60 * 1000 // 5 minutes
};

const HistoryPage = () => {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const { isPremium, trialDaysRemaining } = useMonetization();
  const historyService = HistoryService.getInstance();
  
  // États principaux
  const [activeTab, setActiveTab] = useState('conversations');
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [messages, setMessages] = useState([]);
  const [stories, setStories] = useState([]);
  const [allStories, setAllStories] = useState([]); // Toutes les histoires chargées
  const [displayedStoriesCount, setDisplayedStoriesCount] = useState(5); // Nombre d'histoires affichées
  const [hasMoreStories, setHasMoreStories] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [childStats, setChildStats] = useState(null);
  
  // États pour la gestion du feedback depuis l'historique
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [additionalResponses, setAdditionalResponses] = useState([]);
  
  // Fonction pour normaliser les anciennes histoires au nouveau format
  const normalizeStoryFormat = (story) => {
    if (!story) return null;
    if (story.pages && story.pages.length > 0) {
      return story;
    }
    if (story.content) {
      return {
        ...story,
        pages: [{ page: 1, content: story.content }],
        page_illustrations: story.illustration_base64 ? { '1': story.illustration_base64 } : {}
      };
    }
    return story;
  };

  useEffect(() => {
    fetchChildren();
    if (activeTab === 'conversations') {
      loadMessages();
    } else {
      loadStories();
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'conversations') {
      loadMessages();
    } else {
      loadStories();
    }
    if (selectedChild !== 'all') {
      loadChildStats();
    }
  }, [selectedChild, searchQuery, filterType, activeTab]);

  const fetchChildren = async () => {
    try {
      const response = await api.get('/children');
      setChildren(response.data.children);
    } catch (error) {
      console.error('Error fetching children:', error);
    }
  };

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/conversations');
      let conversations = response.data?.conversations || [];
      
      if (selectedChild !== 'all') {
        conversations = conversations.filter(conv => conv.child_id === selectedChild);
      }
      
      const result = conversations.flatMap(conv => 
        conv.messages
          ?.filter(msg => 
            msg.role === 'user' && 
            !msg.content.includes('L\'enfant souhaite plus d\'informations') && 
            !msg.content.includes('❓') &&
            !msg.content.startsWith('❓')
          )
          ?.map(userMsg => {
            let aiResponse = null;
            let additionalResponses = [];
            
            if (userMsg.message_order !== null && userMsg.message_order !== undefined) {
              aiResponse = conv.messages.find(aiMsg => 
                aiMsg.role === 'assistant' && 
                aiMsg.message_order === (userMsg.message_order + 1)
              );
              
              additionalResponses = conv.messages.filter(aiMsg => 
                aiMsg.role === 'assistant' && 
                aiMsg.message_order > (userMsg.message_order + 1) &&
                (aiMsg.feedback_type === 'more_info' || aiMsg.feedback_type === 'too_hard' || aiMsg.is_additional_info)
              );
            }
            
            if (!aiResponse) {
              const userIndex = conv.messages.findIndex(msg => msg.id === userMsg.id);
              if (userIndex !== -1 && userIndex < conv.messages.length - 1) {
                const nextMessage = conv.messages[userIndex + 1];
                if (nextMessage && nextMessage.role === 'assistant') {
                  if (!nextMessage.content.includes('L\'enfant souhaite plus d\'informations') && 
                      !nextMessage.content.includes('❓') &&
                      !nextMessage.content.startsWith('❓')) {
                    aiResponse = nextMessage;
                    
                    for (let i = userIndex + 2; i < conv.messages.length; i++) {
                      const msg = conv.messages[i];
                      if (msg.role === 'assistant' && 
                          (msg.feedback_type === 'more_info' || msg.feedback_type === 'too_hard' || 
                           msg.is_additional_info ||
                           msg.content.startsWith('Aah tu veux en savoir plus') ||
                           msg.content.startsWith('Laisse moi t\'expliquer autrement'))) {
                        additionalResponses.push(msg);
                      } else if (msg.role === 'user') {
                        break;
                      }
                    }
                  }
                }
              }
            }
            
            if (!aiResponse) {
              const userTimestamp = new Date(userMsg.timestamp);
              aiResponse = conv.messages.find(aiMsg => 
                aiMsg.role === 'assistant' &&
                aiMsg.id !== userMsg.id &&
                !aiMsg.content.includes('L\'enfant souhaite plus d\'informations') &&
                !aiMsg.content.includes('❓') &&
                Math.abs(new Date(aiMsg.timestamp) - userTimestamp) < 60000
              );
            }
            
            let fullAnswer = aiResponse?.content || 'Réponse non disponible';
            if (additionalResponses.length > 0) {
              const feedbackTexts = additionalResponses.map(resp => resp.content).join('\n\n');
              fullAnswer += '\n\n' + feedbackTexts;
            }
            
            return {
              id: userMsg.id || userMsg._id,
              question: userMsg.content,
              answer: fullAnswer,
              timestamp: userMsg.timestamp || conv.created_at,
              childId: conv.child_id,
              childName: children.find(c => c.id === conv.child_id)?.name || conv.child_name || 'Enfant',
              feedback: userMsg.feedback || null,
              favorite: userMsg.favorite || false
            };
          }) || []
      );
      
      let filteredResult = result;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filteredResult = result.filter(msg => 
          msg.question.toLowerCase().includes(query) || 
          msg.answer.toLowerCase().includes(query)
        );
      }
      
      if (filterType === 'favorites') {
        filteredResult = filteredResult.filter(msg => msg.favorite);
      }
      
      filteredResult.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setMessages(filteredResult);
      
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      let result = [];
      if (searchQuery.trim()) {
        result = historyService.searchHistory(searchQuery, selectedChild === 'all' ? null : selectedChild);
      } else if (filterType === 'favorites') {
        result = selectedChild === 'all' 
          ? children.flatMap(child => historyService.getChildFavorites(child.id))
          : historyService.getChildFavorites(selectedChild);
      } else {
        result = historyService.getChildHistory(selectedChild === 'all' ? 'all' : selectedChild, 50);
      }
      setMessages(result);
    }
    setIsLoading(false);
  };

  const loadStories = async () => {
    // Vérifier le cache
    const now = Date.now();
    const cacheValid = storiesCache.data.length > 0 &&
      storiesCache.timestamp &&
      (now - storiesCache.timestamp) < storiesCache.CACHE_DURATION &&
      storiesCache.childFilter === selectedChild;
    
    if (cacheValid && !searchQuery && filterType === 'all') {
      console.log('📚 Utilisation du cache pour les histoires');
      setAllStories(storiesCache.data);
      setStories(storiesCache.data.slice(0, displayedStoriesCount));
      setHasMoreStories(storiesCache.data.length > displayedStoriesCount);
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedChild !== 'all') {
        params.append('child_id', selectedChild);
      }

      const response = await api.get(`/stories?${params.toString()}`);
      let fetchedStories = response.data.stories || [];
      
      // Mettre à jour le cache
      storiesCache.data = fetchedStories;
      storiesCache.timestamp = now;
      storiesCache.childFilter = selectedChild;
      
      // Appliquer les filtres
      let filteredStories = fetchedStories;
      
      if (searchQuery) {
        filteredStories = filteredStories.filter(story => 
          story.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          story.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          story.theme?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      if (filterType === 'favorites') {
        filteredStories = filteredStories.filter(story => story.is_favorite);
      }
      
      setAllStories(filteredStories);
      setStories(filteredStories.slice(0, displayedStoriesCount));
      setHasMoreStories(filteredStories.length > displayedStoriesCount);
    } catch (error) {
      console.error('Erreur lors du chargement des histoires:', error);
      setStories([]);
      setAllStories([]);
      setHasMoreStories(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildStats = async () => {
    if (selectedChild !== 'all') {
      try {
        const response = await api.get('/conversations');
        const conversations = response.data?.conversations || [];
        const childConversations = conversations.filter(conv => conv.child_id === selectedChild);
        
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const todayConversations = childConversations.filter(conv => {
          const convDate = new Date(conv.created_at);
          return convDate >= todayStart;
        });
        
        setChildStats({
          totalQuestions: childConversations.length,
          questionsToday: todayConversations.length,
        });
      } catch (error) {
        console.error('Erreur chargement stats enfant:', error);
        const stats = historyService.getChildStats(selectedChild);
        setChildStats(stats);
      }
    } else {
      setChildStats(null);
    }
  };

  const handleToggleStoryFavorite = async (storyId) => {
    try {
      const favoriteResponse = await api.put(`/stories/${storyId}/favorite`);
      const newFavoriteStatus = favoriteResponse.data.is_favorite;
      setStories(prev => prev.map(item => 
        item.id === storyId ? { ...item, is_favorite: newFavoriteStatus } : item
      ));
    } catch (error) {
      console.error('Erreur toggle favori histoire:', error);
    }
  };

  const handleToggleFavorite = async (messageId) => {
    try {
      const response = await api.get('/conversations');
      const conversations = response.data?.conversations || [];
      
      let conversationId = null;
      for (const conv of conversations) {
        const foundMessage = conv.messages?.find(msg => msg.id === messageId);
        if (foundMessage) {
          conversationId = conv.id;
          break;
        }
      }
      
      if (!conversationId) return;
      
      const favoriteResponse = await api.put(`/conversations/${conversationId}/messages/${messageId}/favorite`);
      const newFavoriteStatus = favoriteResponse.data.favorite;
      
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, favorite: newFavoriteStatus } : msg
      ));

      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, favorite: newFavoriteStatus }));
      }
    } catch (error) {
      console.error('Erreur toggle favori:', error);
    }
  };

  const formatDate = (date) => {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (messageDate.toDateString() === today.toDateString()) {
      return `Aujourd'hui à ${messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return `Hier à ${messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return messageDate.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const handleHistoryFeedback = async (messageId, feedback) => {
    if (feedbackLoading) return;
    
    setFeedbackLoading(true);
    try {
      const message = selectedMessage;
      const childInfo = getChildInfo(message.childId);
      
      if (!childInfo) throw new Error('Enfant introuvable');

      if (feedback === 'more_info') {
        const response = await api.post('/chat', {
          message: `Reprenez votre réponse précédente puis ajoutez "Aah tu veux en savoir plus, super !" suivi de détails supplémentaires.`,
          child_id: childInfo.id,
          continue_conversation: true,
          context: message.question,
          feedback_type: "more_info",
          original_response: message.answer
        });

        if (response.data?.response) {
          const newResponse = {
            id: Date.now().toString(),
            content: response.data.response,
            feedback_type: 'more_info',
            timestamp: new Date().toISOString(),
            is_additional_info: true
          };
          setAdditionalResponses(prev => [...prev, newResponse]);
        }
      } else if (feedback === 'too_hard') {
        const response = await api.post('/chat', {
          message: `Reprenez votre réponse précédente puis ajoutez "Laisse moi t'expliquer autrement !" suivi d'une explication plus simple et adaptée à l'âge.`,
          child_id: childInfo.id,
          continue_conversation: true,
          context: message.question,
          feedback_type: "too_hard",
          original_response: message.answer
        });

        if (response.data?.response) {
          const newResponse = {
            id: Date.now().toString(),
            content: response.data.response,
            feedback_type: 'too_hard',
            timestamp: new Date().toISOString(),
            is_additional_info: true
          };
          setAdditionalResponses(prev => [...prev, newResponse]);
        }
      }
      
      setSelectedMessage(prev => ({ ...prev, feedback: feedback }));
    } catch (error) {
      console.error('Erreur feedback historique:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const getChildInfo = (childId) => {
    return children.find(c => c.id === childId);
  };

  const translateTheme = (theme) => {
    const themes = {
      'animals': 'Animaux', 'adventure': 'Aventure', 'fairy_tale': 'Conte de fées',
      'science': 'Science', 'friendship': 'Amitié', 'family': 'Famille',
      'nature': 'Nature', 'magic': 'Magie', 'space': 'Espace',
      'underwater': 'Sous-marin', 'dinosaurs': 'Dinosaures', 'pirates': 'Pirates',
      'princesses': 'Princesses', 'robots': 'Robots', 'dragons': 'Dragons'
    };
    return themes[theme] || theme;
  };

  const translateStoryType = (storyType) => {
    const types = {
      'adventure': 'Aventure', 'educational': 'Éducative', 'bedtime': 'Coucher',
      'funny': 'Amusante', 'mystery': 'Mystère', 'fantasy': 'Fantastique',
      'realistic': 'Réaliste', 'moral': 'Morale', 'interactive': 'Interactive', 'classic': 'Classique'
    };
    return types[storyType] || storyType;
  };

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');`}
      </style>
      
      <div className="min-h-screen pb-24 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        
        {/* Bulle de texte style BD */}
        <div className="absolute left-4 sm:left-8 top-4 sm:top-8 z-30">
          <div className="relative">
            <svg viewBox="0 0 220 90" className="w-[140px] sm:w-[180px] h-auto" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}>
              <path d="M 20,45 C 20,20 40,8 80,8 C 120,5 160,10 190,20 C 210,28 215,45 210,60 C 205,75 180,82 140,85 C 100,88 60,85 35,75 L 15,88 L 25,70 C 18,65 20,55 20,45 Z" fill="white" stroke="#374151" strokeWidth="3"/>
            </svg>
            <p className="absolute top-1/2 left-1/2 text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 whitespace-nowrap" style={{ fontFamily: '"Caveat", cursive', transform: 'translate(-52%, -45%) rotate(-1deg)' }}>
              Dis Maman !
            </p>
          </div>
        </div>

        {/* Mascotte qui salue - Même taille que ChatPage, en arrière-plan */}
        <div className="absolute right-0 top-0 z-0 pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_kidchat-5/artifacts/eoqf2rki_image.png"
            alt="Mascotte"
            className="h-[340px] sm:h-[440px] md:h-[520px] lg:h-[600px] object-contain"
            style={{ filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.2))' }}
          />
        </div>
        
        {/* Header épuré avec onglets - Décalé vers le bas */}
        <div className="sticky top-0 z-20 pt-28 sm:pt-32 px-4 bg-gradient-to-b from-[#B8D4E3]/90 to-transparent">
          <div className="max-w-2xl mx-auto">
            {/* Titre */}
            <h1 
              className="text-3xl font-bold text-center text-gray-800 mb-4"
              style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
            >
              Mon Historique
            </h1>
            
            {/* Onglets - Style app mobile */}
            <div className="flex justify-center gap-2 mb-4">
              <button
                onClick={() => setActiveTab('conversations')}
                className="px-6 py-2.5 rounded-xl font-medium transition-all duration-200"
                style={{
                  background: '#FFFFFF',
                  border: activeTab === 'conversations' ? '2px solid #4A90A4' : '2px solid rgba(91, 191, 189, 0.3)',
                  color: activeTab === 'conversations' ? '#4A90A4' : '#666666',
                  boxShadow: activeTab === 'conversations' ? '0 4px 12px rgba(74, 144, 164, 0.2)' : 'none'
                }}
              >
                💬 Questions
              </button>
              <button
                onClick={() => setActiveTab('stories')}
                className="px-6 py-2.5 rounded-xl font-medium transition-all duration-200"
                style={{
                  background: '#FFFFFF',
                  border: activeTab === 'stories' ? '2px solid #4A90A4' : '2px solid rgba(91, 191, 189, 0.3)',
                  color: activeTab === 'stories' ? '#4A90A4' : '#666666',
                  boxShadow: activeTab === 'stories' ? '0 4px 12px rgba(74, 144, 164, 0.2)' : 'none'
                }}
              >
                📖 Histoires
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-2 relative z-10">
          <div className="max-w-2xl mx-auto space-y-4">
            
            {/* Filtres - Style app mobile */}
            <div className="bg-white rounded-2xl p-4 shadow-lg">
              {/* Sélection enfant - Style pill */}
              <div className="mb-4">
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    onClick={() => setSelectedChild('all')}
                    className="px-4 py-2 rounded-full transition-all duration-200"
                    style={{
                      background: selectedChild === 'all' ? 'rgba(74, 144, 164, 0.1)' : '#FFFFFF',
                      border: selectedChild === 'all' ? '2px solid #4A90A4' : '2px solid rgba(91, 191, 189, 0.3)',
                      color: selectedChild === 'all' ? '#4A90A4' : '#666666',
                      fontFamily: '"Caveat", cursive',
                      fontSize: '1.15rem'
                    }}
                  >
                    Tous
                  </button>
                  {children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => setSelectedChild(child.id)}
                      className="px-4 py-2 rounded-full transition-all duration-200"
                      style={{
                        background: selectedChild === child.id ? 'rgba(74, 144, 164, 0.1)' : '#FFFFFF',
                        border: selectedChild === child.id ? '2px solid #4A90A4' : '2px solid rgba(91, 191, 189, 0.3)',
                        color: selectedChild === child.id ? '#4A90A4' : '#666666',
                        fontFamily: '"Caveat", cursive',
                        fontSize: '1.15rem'
                      }}
                    >
                      {child.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recherche et filtres sur la même ligne */}
              <div className="flex gap-2 items-center">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="🔍 Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border-2 bg-white focus:outline-none transition-colors text-sm"
                    style={{ borderColor: 'rgba(91, 191, 189, 0.3)' }}
                  />
                </div>
                <button
                  onClick={() => setFilterType(filterType === 'favorites' ? 'all' : 'favorites')}
                  className="px-4 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-1"
                  style={{
                    background: filterType === 'favorites' ? 'rgba(74, 144, 164, 0.15)' : '#FFFFFF',
                    border: '2px solid rgba(91, 191, 189, 0.3)',
                    color: filterType === 'favorites' ? '#4A90A4' : '#666666'
                  }}
                >
                  {filterType === 'favorites' ? '⭐' : '☆'} Favoris
                </button>
              </div>

              {/* Filtres actifs */}
              {(selectedChild !== 'all' || filterType === 'favorites' || searchQuery) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedChild !== 'all' && (
                    <span 
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-1 cursor-pointer"
                      style={{ background: 'rgba(74, 144, 164, 0.15)', color: '#4A90A4' }}
                      onClick={() => setSelectedChild('all')}
                    >
                      {children.find(c => c.id === selectedChild)?.name} ✕
                    </span>
                  )}
                  {filterType === 'favorites' && (
                    <span 
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-1 cursor-pointer"
                      style={{ background: 'rgba(74, 144, 164, 0.15)', color: '#4A90A4' }}
                      onClick={() => setFilterType('all')}
                    >
                      Favoris ✕
                    </span>
                  )}
                  {searchQuery && (
                    <span 
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-1 cursor-pointer"
                      style={{ background: 'rgba(74, 144, 164, 0.15)', color: '#4A90A4' }}
                      onClick={() => setSearchQuery('')}
                    >
                      "{searchQuery}" ✕
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Statistiques enfant */}
            {childStats && selectedChild !== 'all' && (
              <div className="bg-white/60 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-white/50">
                <h3 className="font-bold text-gray-800 mb-3 text-center">
                  📊 Stats de {children.find(c => c.id === selectedChild)?.name}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50/80 p-3 rounded-xl text-center">
                    <div className="text-2xl font-bold text-blue-600">{childStats.totalQuestions}</div>
                    <div className="text-xs text-gray-600">Questions totales</div>
                  </div>
                  <div className="bg-green-50/80 p-3 rounded-xl text-center">
                    <div className="text-2xl font-bold text-green-600">{childStats.questionsToday}</div>
                    <div className="text-xs text-gray-600">Aujourd'hui</div>
                  </div>
                </div>
              </div>
            )}

            {/* Liste des éléments */}
            <div className="space-y-3">
              {isLoading ? (
                <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 text-center shadow-lg border border-white/50">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-teal-200 border-t-teal-500 mx-auto mb-3"></div>
                  <span className="text-gray-600">Chargement...</span>
                </div>
              ) : activeTab === 'conversations' ? (
                messages.length === 0 ? (
                  <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 text-center shadow-lg border border-white/50">
                    <div className="text-5xl mb-4">🤷‍♀️</div>
                    <h3 className="font-bold text-gray-800 mb-2">Aucune conversation</h3>
                    <p className="text-gray-600 text-sm">
                      {searchQuery ? "Essaie d'autres mots-clés !" :
                       filterType === 'favorites' ? "Aucun favori pour l'instant." :
                       "Pose ta première question !"}
                    </p>
                  </div>
                ) : (
                  messages.map((message) => {
                    const childInfo = getChildInfo(message.childId);
                    return (
                      <div
                        key={message.id}
                        onClick={() => {
                          setSelectedMessage(message);
                          setAdditionalResponses([]);
                          setShowMessageModal(true);
                        }}
                        className="bg-white rounded-2xl p-4 shadow-lg cursor-pointer transition-all duration-200 hover:shadow-xl hover:scale-[1.01]"
                      >
                        {/* Header avec nom, compteur et date */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span 
                              className="font-bold"
                              style={{ fontFamily: '"Caveat", cursive', fontSize: '1.25rem', color: '#333333' }}
                            >
                              {message.childName}
                            </span>
                            <span 
                              className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: 'rgba(74, 144, 164, 0.15)', color: '#4A90A4' }}
                            >
                              1 échange
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {message.favorite && <span className="text-yellow-500">⭐</span>}
                            <span className="text-xs text-gray-500">{formatDate(message.timestamp)}</span>
                          </div>
                        </div>
                        
                        {/* Question - Style orange */}
                        <p 
                          className="text-sm mb-2 line-clamp-1"
                          style={{ color: '#E67E22', fontWeight: 600 }}
                        >
                          ❓ {message.question}
                        </p>
                        
                        {/* Réponse - Style gris */}
                        <p 
                          className="text-sm line-clamp-2"
                          style={{ color: '#666666' }}
                        >
                          💬 {message.answer.substring(0, 120)}...
                        </p>
                      </div>
                    );
                  })
                )
              ) : (
                // Histoires
                stories.length === 0 ? (
                  <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 text-center shadow-lg border border-white/50">
                    <div className="text-5xl mb-4">📚</div>
                    <h3 className="font-bold text-gray-800 mb-2">Aucune histoire</h3>
                    <p className="text-gray-600 text-sm">
                      {searchQuery ? "Essaie d'autres mots-clés !" :
                       filterType === 'favorites' ? "Aucune histoire favorite." :
                       "Crée ta première histoire !"}
                    </p>
                  </div>
                ) : (
                  stories.map((story) => {
                    const childInfo = story.children_ids && story.children_ids.length > 0 
                      ? getChildInfo(story.children_ids[0]) 
                      : null;
                    const firstIllustration = story.page_illustrations 
                      ? story.page_illustrations["1"] || story.page_illustrations[1]
                      : story.illustration_base64;
                    
                    return (
                      <div
                        key={story._id}
                        onClick={() => {
                          navigate('/story/view', { 
                            state: { 
                              story: normalizeStoryFormat(story), 
                              pageIllustrations: story.page_illustrations || {},
                              fromHistory: true
                            } 
                          });
                        }}
                        className="bg-white rounded-2xl p-4 shadow-lg cursor-pointer transition-all duration-200 hover:shadow-xl hover:scale-[1.01]"
                      >
                        <div className="flex items-start gap-3">
                          {/* Thumbnail */}
                          {firstIllustration ? (
                            <img
                              src={`data:image/png;base64,${firstIllustration}`}
                              alt="Miniature"
                              className="w-16 h-16 rounded-xl object-cover shadow-md flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-3xl">📖</span>
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            {/* Titre et favori */}
                            <div className="flex items-center justify-between mb-2">
                              <h3 
                                className="truncate pr-2"
                                style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem', fontWeight: 700, color: '#333333' }}
                              >
                                {story.title || 'Histoire sans titre'}
                              </h3>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleStoryFavorite(story.id);
                                }}
                                className="text-lg transition-transform hover:scale-110 flex-shrink-0"
                              >
                                {story.is_favorite ? '⭐' : '☆'}
                              </button>
                            </div>
                            
                            {/* Tags - Style app mobile */}
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              <span 
                                className="px-2 py-0.5 rounded-lg text-xs font-medium"
                                style={{ background: 'rgba(74, 144, 164, 0.1)', color: '#4A90A4' }}
                              >
                                {translateTheme(story.theme)}
                              </span>
                              <span 
                                className="px-2 py-0.5 rounded-lg text-xs font-medium"
                                style={{ background: 'rgba(82, 183, 136, 0.15)', color: '#52B788' }}
                              >
                                {translateStoryType(story.story_type)}
                              </span>
                            </div>
                            
                            {/* Date */}
                            <p className="text-xs text-gray-500">{formatDate(story.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )
              )}
              
              {/* Bouton charger plus d'histoires */}
              {activeTab === 'stories' && hasMoreStories && !isLoading && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => {
                      const newCount = displayedStoriesCount + 5;
                      setDisplayedStoriesCount(newCount);
                      setStories(allStories.slice(0, newCount));
                      setHasMoreStories(allStories.length > newCount);
                    }}
                    disabled={isLoadingMore}
                    className="px-6 py-3 bg-white/80 backdrop-blur-sm rounded-xl font-medium text-teal-700 
                      hover:bg-white transition-all duration-200 shadow-lg border border-white/50"
                  >
                    {isLoadingMore ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-teal-300 border-t-teal-600 rounded-full animate-spin"></div>
                        Chargement...
                      </span>
                    ) : (
                      `📚 Voir plus d'histoires (${allStories.length - displayedStoriesCount} restantes)`
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Message de fin */}
            {messages.length >= 50 && activeTab === 'conversations' && (
              <div className="bg-yellow-50/80 backdrop-blur-sm rounded-2xl p-4 text-center border border-yellow-200/50">
                <p className="text-sm text-yellow-700">
                  📝 50 conversations affichées. Utilise la recherche pour plus !
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Question épuré */}
        {showMessageModal && selectedMessage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Overlay */}
            <div 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setShowMessageModal(false)}
            />
            
            {/* Modal content */}
            <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-6">
              {/* Bouton fermer */}
              <button
                onClick={() => setShowMessageModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
              >
                ✕
              </button>

              {/* Header */}
              <div className="flex items-center gap-3 mb-6 pr-8">
                <DisAvatar 
                  name={selectedMessage.childName} 
                  gender={getChildInfo(selectedMessage.childId)?.gender} 
                  size="md"
                  colorIndex={children.findIndex(c => c.id === selectedMessage.childId)}
                />
                <div>
                  <h3 className="font-bold text-gray-800">{selectedMessage.childName}</h3>
                  <p className="text-xs text-gray-500">{formatDate(selectedMessage.timestamp)}</p>
                </div>
                <button
                  onClick={() => handleToggleFavorite(selectedMessage.id)}
                  className={`ml-auto text-xl transition-transform hover:scale-110 ${
                    selectedMessage.favorite ? 'text-yellow-500' : 'text-gray-400'
                  }`}
                >
                  {selectedMessage.favorite ? '⭐' : '☆'}
                </button>
              </div>

              {/* Question - Style app mobile (fond orange) */}
              <div 
                className="rounded-2xl p-4 mb-4"
                style={{ 
                  background: 'rgba(230, 126, 34, 0.08)', 
                  borderLeft: '4px solid #E67E22' 
                }}
              >
                <h4 className="font-bold mb-2 text-sm" style={{ color: '#E67E22' }}>❓ Question :</h4>
                <p className="text-gray-800">{selectedMessage.question}</p>
              </div>

              {/* Réponse - Style app mobile (fond bleu) */}
              <div 
                className="rounded-2xl p-4 mb-4"
                style={{ 
                  background: 'rgba(74, 144, 164, 0.08)', 
                  borderLeft: '4px solid #4A90A4' 
                }}
              >
                <h4 className="font-bold mb-2 text-sm" style={{ color: '#4A90A4' }}>💬 Réponse :</h4>
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{selectedMessage.answer}</p>
              </div>

              {/* Réponses supplémentaires - Style app mobile */}
              {additionalResponses.length > 0 && (
                <div className="space-y-3 mb-4">
                  {additionalResponses.map((resp) => (
                    <div 
                      key={resp.id} 
                      className="rounded-2xl p-4"
                      style={{ 
                        background: 'rgba(74, 144, 164, 0.08)', 
                        borderLeft: '4px solid #4A90A4' 
                      }}
                    >
                      <div 
                        className="inline-block px-2 py-0.5 rounded text-xs mb-2"
                        style={{ background: 'rgba(0,0,0,0.05)', color: '#999999' }}
                      >
                        {resp.feedback_type === 'more_info' ? 'Plus d\'infos' : 'Simplifié'}
                      </div>
                      <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{resp.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Feedback existant */}
              {selectedMessage.feedback && !additionalResponses.length && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-4 mb-4 border border-yellow-100">
                  <h4 className="font-bold text-orange-800 mb-2 text-sm">📊 Feedback :</h4>
                  <p className="text-gray-800">
                    {selectedMessage.feedback === 'understood' ? '👍 Compris !' :
                     selectedMessage.feedback === 'too_hard' ? '🤔 Trop compliqué' :
                     selectedMessage.feedback === 'more_info' ? '🧐 Plus d\'infos demandées' :
                     selectedMessage.feedback}
                  </p>
                </div>
              )}

              {/* Boutons de feedback */}
              {!selectedMessage.feedback && !feedbackLoading && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600 text-center mb-3">
                    Voulez-vous plus d'informations ?
                  </p>
                  <FeedbackButtons
                    messageId={selectedMessage.id}
                    childAge={getChildInfo(selectedMessage.childId)?.age || 8}
                    onFeedbackGiven={(feedback) => handleHistoryFeedback(selectedMessage.id, feedback)}
                    disabled={feedbackLoading}
                  />
                </div>
              )}
              
              {/* Loading feedback */}
              {feedbackLoading && (
                <div className="border-t border-gray-200 pt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-4 border-teal-200 border-t-teal-500 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Génération en cours...</p>
                </div>
              )}

              {/* Bouton fermer */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-8 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Menu flottant */}
        <FloatingMenu />
      </div>
    </>
  );
};

export default HistoryPage;
